import React from 'react';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type WeeklyRevenueChartProps = {
    allClosedJobs: any[];
    loading?: boolean;
};

export const WeeklyRevenueChart: React.FC<WeeklyRevenueChartProps> = ({ allClosedJobs, loading }) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    const calculateDealerRevenue = (jobs: any[]) => {
        return jobs.reduce((sum, job) => {
            if (!job.selectedTreatments) return sum;
            return sum + job.selectedTreatments.reduce((s: number, t: any) => s + (t.dealerPrice || 0), 0);
        }, 0);
    };

    const calculateCustomerRevenue = (jobs: any[]) => {
        return jobs.reduce((sum, job) => {
            if (!job.selectedTreatments) return sum;
            return sum + job.selectedTreatments.reduce((s: number, t: any) => s + (t.customerPrice || 0), 0);
        }, 0);
    };

    // Get day performance
    const getDayPerformance = (jobs: any[]) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayRevenue = new Array(7).fill(0);
        const dayCounts = new Array(7).fill(0);

        jobs.forEach(job => {
            const date = new Date(job.endDate || job.committedDate);
            const dayIdx = date.getDay();
            dayRevenue[dayIdx] += calculateCustomerRevenue([job]);
            dayCounts[dayIdx]++;
        });

        const maxRev = Math.max(...dayRevenue);
        const minRev = Math.min(...dayRevenue.filter(r => r > 0));

        const bestDayIndex = dayRevenue.indexOf(maxRev);
        const slowestDayIndex = dayRevenue.indexOf(minRev === Infinity ? 0 : minRev);

        return {
            bestDay: days[bestDayIndex],
            bestRevenue: maxRev,
            slowestDay: days[slowestDayIndex],
            slowestRevenue: minRev === Infinity ? 0 : minRev,
            dayRevenue: dayRevenue,
            dayCounts: dayCounts
        };
    };

    // Filter jobs from last 30 days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const last30DaysStart = new Date(today);
    last30DaysStart.setDate(today.getDate() - 30);

    const filterJobsByDateRange = (jobs: any[], start: Date, end: Date) => {
        return jobs.filter(job => {
            const jobDate = new Date(job.endDate || job.committedDate);
            jobDate.setHours(0, 0, 0, 0);
            return jobDate >= start && jobDate <= end;
        });
    };

    const last30DaysJobs = filterJobsByDateRange(allClosedJobs, last30DaysStart, today);
    const dayStats = getDayPerformance(last30DaysJobs);

    return (
        <div className="card h-100" style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff' }}>
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className={`card-label fw-bolder fs-3 ${isDarkMode ? 'text-white' : 'text-dark'}`}>
                        <i className="bi bi-bar-chart-fill me-2 text-primary"></i>
                        Weekly Revenue
                    </span>
                    <span className="text-muted mt-1 fw-bold fs-7">Revenue distribution by day of week</span>
                </h3>
            </div>
            <div className="card-body pt-3">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '200px'}}>
                        <span className="spinner-border text-primary"></span>
                    </div>
                ) : (
                    <>
                        {/* Enhanced Visual Week Graph */}
                        <div className="d-flex align-items-end justify-content-between" style={{ height: '180px', paddingTop: '20px' }}>
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => {
                                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                const revenue = dayStats.dayRevenue[i];
                                const maxRevenue = Math.max(...dayStats.dayRevenue);
                                const heightPercentage = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                                const barHeight = Math.max((heightPercentage * 1.2), revenue > 0 ? 30 : 10);
                                const isBestDay = dayNames[i] === dayStats.bestDay;
                                const isSlowestDay = dayNames[i] === dayStats.slowestDay;

                                return (
                                    <div
                                        key={i}
                                        className="d-flex flex-column align-items-center"
                                        style={{ flex: 1, position: 'relative', gap: '12px' }}
                                    >
                                        {/* Bar with gradient and animation */}
                                        <div
                                            className="rounded"
                                            style={{
                                                width: '100%',
                                                maxWidth: '45px',
                                                height: `${barHeight}px`,
                                                background: isBestDay
                                                    ? 'linear-gradient(180deg, #50cd89 0%, #47be7d 100%)'
                                                    : isSlowestDay
                                                    ? 'linear-gradient(180deg, #f1416c 0%, #d63652 100%)'
                                                    : isDarkMode
                                                    ? 'linear-gradient(180deg, #4776E6 0%, #8E54E9 100%)'
                                                    : 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                                                boxShadow: isBestDay || isSlowestDay
                                                    ? `0 4px 12px ${isBestDay ? 'rgba(80, 205, 137, 0.4)' : 'rgba(241, 65, 108, 0.4)'}`
                                                    : isDarkMode
                                                    ? '0 2px 8px rgba(71, 118, 230, 0.3)'
                                                    : '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                                                e.currentTarget.style.boxShadow = isBestDay
                                                    ? '0 8px 20px rgba(80, 205, 137, 0.6)'
                                                    : isSlowestDay
                                                    ? '0 8px 20px rgba(241, 65, 108, 0.6)'
                                                    : isDarkMode
                                                    ? '0 6px 16px rgba(71, 118, 230, 0.5)'
                                                    : '0 6px 16px rgba(102, 126, 234, 0.5)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                e.currentTarget.style.boxShadow = isBestDay || isSlowestDay
                                                    ? `0 4px 12px ${isBestDay ? 'rgba(80, 205, 137, 0.4)' : 'rgba(241, 65, 108, 0.4)'}`
                                                    : isDarkMode
                                                    ? '0 2px 8px rgba(71, 118, 230, 0.3)'
                                                    : '0 2px 8px rgba(102, 126, 234, 0.3)';
                                            }}
                                            title={`${dayNames[i]}: ₹${Math.round(revenue).toLocaleString()} (${dayStats.dayCounts[i]} jobs)`}
                                        >
                                            {/* Shimmer effect */}
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: '-100%',
                                                width: '100%',
                                                height: '100%',
                                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                                animation: `shimmer${i} 3s infinite`,
                                            }} />
                                        </div>

                                        {/* Day label */}
                                        <span
                                            className="fw-bold"
                                            style={{
                                                fontSize: '11px',
                                                color: isBestDay
                                                    ? '#50cd89'
                                                    : isSlowestDay
                                                    ? '#f1416c'
                                                    : isDarkMode
                                                    ? 'rgba(255, 255, 255, 0.7)'
                                                    : 'rgba(0, 0, 0, 0.6)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            {day}
                                        </span>

                                        {/* Best/Worst day indicator */}
                                        {(isBestDay || isSlowestDay) && (
                                            <div style={{
                                                fontSize: '8px',
                                                color: isBestDay ? '#50cd89' : '#f1416c',
                                                fontWeight: 'bold'
                                            }}>
                                                {isBestDay ? '★' : '▼'}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Legend */}
                        <div className="d-flex justify-content-center gap-4 mt-4">
                            <div className="d-flex align-items-center">
                                <div className="rounded" style={{
                                    width: '12px',
                                    height: '12px',
                                    background: 'linear-gradient(180deg, #50cd89 0%, #47be7d 100%)',
                                    marginRight: '6px'
                                }}></div>
                                <span className={`fs-8 ${isDarkMode ? 'text-white' : 'text-muted'}`}>Best Day</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="rounded" style={{
                                    width: '12px',
                                    height: '12px',
                                    background: 'linear-gradient(180deg, #f1416c 0%, #d63652 100%)',
                                    marginRight: '6px'
                                }}></div>
                                <span className={`fs-8 ${isDarkMode ? 'text-white' : 'text-muted'}`}>Slowest Day</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Add shimmer animation keyframes */}
            <style>{`
                @keyframes shimmer0 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                @keyframes shimmer1 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                @keyframes shimmer2 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                @keyframes shimmer3 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                @keyframes shimmer4 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                @keyframes shimmer5 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                @keyframes shimmer6 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
            `}</style>
        </div>
    );
};
